import * as styles from "./DataTableHeaderCell.css";

import type { JSX, ReactNode } from "react";

import { Icon } from "@frontend/components/atoms/Icon";
import { useForesight } from "@frontend/hooks/useForesight";
import type { Column, RowData, SortingState } from "@tanstack/react-table";

import { PREFETCH_REACTIVATE_MS } from "./constants";
import type { dataTableFeatures } from "./dataTableFeatures";

type DataTableSortButtonProps<TData extends RowData> = {
  column: Column<typeof dataTableFeatures, TData, unknown>;
  direction: "asc" | "desc" | false;
  label: ReactNode;
  onClick: ((event: unknown) => void) | undefined;
  onSortPrefetch?: (sorting: SortingState) => void;
};

export function DataTableSortButton<TData extends RowData>({
  column,
  direction,
  label,
  onClick,
  onSortPrefetch,
}: DataTableSortButtonProps<TData>): JSX.Element {
  const descFirst = true === column.columnDef.sortDescFirst;

  const firstOrder = descFirst ? "desc" : "asc";
  const secondOrder = descFirst ? "asc" : "desc";

  const next =
    false === direction
      ? firstOrder
      : direction === firstOrder
        ? secondOrder
        : false;

  const { elementRef, onFocus } = useForesight<HTMLButtonElement>({
    name: `Sort ${column.id} ${false === next ? "clear" : next}`,
    reactivateAfter: PREFETCH_REACTIVATE_MS,
    callback: onSortPrefetch
      ? () =>
          onSortPrefetch(
            false === next ? [] : [{ id: column.id, desc: "desc" === next }],
          )
      : undefined,
  });

  return (
    <button
      ref={elementRef}
      onFocus={onFocus}
      type="button"
      className={styles.sortButton}
      onClick={onClick}
    >
      {label}

      <Icon
        name={
          "asc" === direction
            ? "ChevronUp"
            : "desc" === direction
              ? "ChevronDown"
              : "ChevronsUpDown"
        }
        size="sm"
        className={styles.sortIcon({ active: false !== direction })}
      />
    </button>
  );
}
